import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { Calendar } from "../components/ui/calendar";
import { Users, MapPin, Tag, X } from "lucide-react";

const dateOnly = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const toISODate = (d) => d.toISOString().slice(0, 10);

function expandRange(startStr, endStr) {
  const out = [];
  const start = new Date(startStr);
  const end = new Date(endStr);
  const cur = new Date(start);
  while (cur < end) {
    out.push(toISODate(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

export default function Booking() {
  const { t, pick, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("spot");
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [bookedDates, setBookedDates] = useState([]);
  const [range, setRange] = useState();
  const [guests, setGuests] = useState(1);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/items").then((r) => setItems(r.data));
  }, []);

  useEffect(() => {
    if (!selected) { setBookedDates([]); return; }
    api.get("/bookings/availability", { params: { item_id: selected.id } }).then((r) => {
      const all = [];
      r.data.forEach((b) => all.push(...expandRange(b.start_date, b.end_date)));
      setBookedDates(all.map((d) => dateOnly(new Date(d))));
    });
  }, [selected]);

  const filtered = items.filter((i) => i.type === tab);

  const nights = useMemo(() => {
    if (!range?.from || !range?.to) return 0;
    return Math.max(0, Math.round((range.to - range.from) / (1000 * 60 * 60 * 24)));
  }, [range]);

  const total = selected && nights ? (nights * selected.price_per_night).toFixed(2) : "0.00";

  const disabledDates = useMemo(() => [
    { before: dateOnly(new Date()) },
    ...bookedDates,
  ], [bookedDates]);

  const submit = async () => {
    if (!user) { navigate("/login"); return; }
    if (!selected || !range?.from || !range?.to) {
      toast.error("Please select dates");
      return;
    }
    setSubmitting(true);
    try {
      const { data: booking } = await api.post("/bookings", {
        item_id: selected.id,
        item_type: selected.type,
        start_date: toISODate(range.from),
        end_date: toISODate(range.to),
        guests,
        notes,
      });
      // Create stripe checkout
      const { data: checkout } = await api.post("/payments/checkout", {
        booking_id: booking.id,
        origin_url: window.location.origin,
      });
      window.location.href = checkout.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="py-20 md:py-28 container-deg" data-testid="booking-page">
      <div className="max-w-2xl mb-12">
        <div className="overline">05 · {t("nav.booking")}</div>
        <h1 className="font-serif text-5xl md:text-6xl mt-4 text-balance">{t("booking.heading")}</h1>
        <p className="text-ink-soft mt-6 leading-relaxed">{t("booking.sub")}</p>
      </div>

      <div className="flex gap-2 border-b border-line mb-10" data-testid="booking-tabs">
        {[["spot", t("booking.tabs_spots")], ["house", t("booking.tabs_houses")]].map(([k, l]) => (
          <button
            key={k}
            onClick={() => { setTab(k); setSelected(null); setRange(undefined); }}
            className={`px-6 py-3 text-sm tracking-wide transition-colors border-b-2 -mb-px ${tab === k ? "border-accent text-primary font-semibold" : "border-transparent text-ink-soft hover:text-primary"}`}
            data-testid={`booking-tab-${k}`}
          >
            {l}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="booking-items-grid">
        {filtered.map((it) => (
          <button
            key={it.id}
            onClick={() => { setSelected(it); setRange(undefined); }}
            className={`card-deg text-left overflow-hidden ${selected?.id === it.id ? "ring-2 ring-accent" : ""}`}
            data-testid={`booking-item-${it.id}`}
          >
            <div className="aspect-[4/3] bg-bg-muted overflow-hidden">
              {it.image_base64 ? <img src={it.image_base64} alt={pick(it, "name")} className="w-full h-full object-cover" /> : (
                <div className="w-full h-full flex items-center justify-center text-secondary">
                  <MapPin size={42} strokeWidth={1} />
                </div>
              )}
            </div>
            <div className="p-5">
              <h3 className="font-serif text-2xl">{pick(it, "name")}</h3>
              <p className="text-sm text-ink-soft mt-2 line-clamp-2">{pick(it, "description")}</p>
              <div className="flex items-center justify-between mt-4 text-sm">
                <div className="flex items-center gap-1 text-secondary">
                  <Users size={14} strokeWidth={1.5} /> {it.capacity}
                </div>
                <div className="font-serif text-xl text-accent">€{it.price_per_night}<span className="text-xs text-ink-soft"> {t("booking.per_night")}</span></div>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Booking panel */}
      {selected && (
        <div className="fixed inset-0 z-[60] bg-primary/60 flex items-center justify-center p-4" data-testid="booking-panel" onClick={() => setSelected(null)}>
          <div className="bg-bg-paper w-full max-w-4xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-8 py-6 border-b border-line">
              <div>
                <div className="overline">{t("booking.pick_dates")}</div>
                <h2 className="font-serif text-3xl mt-1">{pick(selected, "name")}</h2>
              </div>
              <button onClick={() => setSelected(null)} className="text-ink-soft hover:text-primary" data-testid="close-booking-panel"><X size={20} /></button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8">
              <div>
                <Calendar
                  mode="range"
                  selected={range}
                  onSelect={setRange}
                  disabled={disabledDates}
                  numberOfMonths={1}
                  className="bg-bg-paper border border-line"
                  data-testid="booking-calendar"
                />
                <div className="flex gap-6 text-xs mt-4">
                  <span className="flex items-center gap-2"><span className="w-3 h-3 bg-bg-muted border border-line" /> {t("booking.available")}</span>
                  <span className="flex items-center gap-2"><span className="w-3 h-3 bg-primary/20 border border-primary" /> {t("booking.booked")}</span>
                </div>
              </div>
              <div className="space-y-5">
                <p className="text-ink-soft text-sm">{pick(selected, "description")}</p>

                <div className="border-t border-line pt-5">
                  <div className="flex items-center gap-2 text-secondary mb-2">
                    <Tag size={14} strokeWidth={1.5} />
                    <span className="overline">{t("booking.amenities")}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(lang === "hu" ? selected.amenities_hu : selected.amenities_en).map((a) => (
                      <span key={a} className="px-3 py-1 border border-line text-xs">{a}</span>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 border-t border-line pt-5">
                  <div>
                    <label className="overline">{t("booking.guests")}</label>
                    <input type="number" min={1} max={selected.capacity} value={guests} onChange={(e) => setGuests(parseInt(e.target.value) || 1)} className="input-deg mt-2" data-testid="booking-guests" />
                  </div>
                  <div>
                    <label className="overline">{t("booking.capacity")}</label>
                    <div className="mt-2 py-2">{selected.capacity}</div>
                  </div>
                </div>

                <div>
                  <label className="overline">{t("booking.notes")}</label>
                  <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="input-deg mt-2 resize-none" data-testid="booking-notes" />
                </div>

                <div className="border-t border-line pt-5 flex items-end justify-between">
                  <div>
                    <div className="overline">{t("booking.total")}</div>
                    <div className="font-serif text-4xl text-primary mt-1">€{total}</div>
                    <div className="text-xs text-ink-soft mt-1">{nights} {t("booking.nights")}</div>
                  </div>
                </div>

                {!user ? (
                  <Link to="/login" className="btn-primary w-full text-center" data-testid="booking-login-link">{t("booking.login_required")}</Link>
                ) : (
                  <button onClick={submit} disabled={submitting || nights === 0} className="btn-primary w-full disabled:opacity-60" data-testid="booking-submit">
                    {submitting ? t("common.loading") : t("booking.reserve")}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
